import React, { useState, useMemo } from 'react';

import { Button, Input, RadioGroup, showToast } from '@attentive-platform/stem-ui';

import { FormContainer } from '../../styled';
import { fetchApiData } from '../../Utils/ApiCalls';
import { GET_ENABLE_WINDOW, MANAGE_ENABLE_WINDOW } from '../../urls';
import { MODULE_ACTION_ENUM, MODULE_ACTION_ENUM_LABELS, BEAM_MODULES } from '../../Constants/Constants';

const APP_SOURCE_ENUM = {
  FALCON: 1,
  BEAM: 2
};

const APP_SOURCE_OPTIONS = [
  { label: 'Beam', value: APP_SOURCE_ENUM.BEAM },
  { label: 'Falcon BPT', value: APP_SOURCE_ENUM.FALCON }
];

const getRadioGroupOptions = () =>
  Object.entries(MODULE_ACTION_ENUM_LABELS).map(([value, label]) => ({
    label,
    value: Number(value)
  }));

const getInitialModuleState = () => ({
  action: MODULE_ACTION_ENUM.DISABLE,
  start_date: '',
  end_date: ''
});

const getInitialFormData = () => ({
  bid_board: getInitialModuleState(),
  bid_sniper: getInitialModuleState(),
  beam_gpt: getInitialModuleState(),
  bid_track: getInitialModuleState()
});

const EmailInputSection = ({ email, onEmailChange, onProceed, loading, isEmailDisabled, buttonText }) => {
  return (
    <div className='mb-6'>
      <label className='block font-medium text-gray-900 mb-2 text-left'>Company email</label>
      <div className='flex gap-2'>
        <Input
          value={email}
          className='w-full'
          disabled={isEmailDisabled}
          placeholder='Enter company email'
          onChange={e => onEmailChange(e.target.value)}
        />
        <Button color='primary' variant='outlined' onClick={onProceed} loading={loading}>
          {buttonText}
        </Button>
      </div>
    </div>
  );
};

const ModuleRow = ({ module, moduleData, onActionChange, onDateChange, disabled }) => {
  const requiresDates = moduleData.action !== MODULE_ACTION_ENUM.DISABLE;
  const radioOptions = useMemo(() => getRadioGroupOptions(), []);
  const isDateDisabled = disabled || !requiresDates;

  return (
    <div className='mb-6'>
      <div className='flex items-center justify-between mb-4'>
        <label className='font-medium text-gray-900'>{module.label}</label>
        <RadioGroup
          row
          variant='standard'
          disabled={disabled}
          value={moduleData.action}
          radioGroup={radioOptions}
          onChange={(_, newValue) => onActionChange(module.key, newValue)}
        />
      </div>

      <div className='space-y-3'>
        <div className='flex items-center justify-between'>
          <label className='text-sm text-gray-600'>Feature start date</label>
          <Input
            type='date'
            size='small'
            className='w-48'
            disabled={isDateDisabled}
            value={moduleData.start_date || ''}
            onChange={e => onDateChange(module.key, 'start_date', e.target.value)}
          />
        </div>
        <div className='flex items-center justify-between'>
          <label className='text-sm text-gray-600'>Feature end date</label>
          <Input
            type='date'
            size='small'
            className='w-48'
            disabled={isDateDisabled}
            value={moduleData.end_date || ''}
            onChange={e => onDateChange(module.key, 'end_date', e.target.value)}
          />
        </div>
      </div>
    </div>
  );
};

const AppSourceSection = ({ source, onSourceChange, disabled }) => (
  <div className='mb-6'>
    <div className='flex items-center justify-between'>
      <label className='font-medium text-gray-900'>Source</label>
      <RadioGroup
        row
        value={source}
        disabled={disabled}
        variant='standard'
        radioGroup={APP_SOURCE_OPTIONS}
        onChange={(_, newValue) => onSourceChange(newValue)}
      />
    </div>
  </div>
);

const BeamModuleManagement = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [companyId, setCompanyId] = useState(null);
  const [source, setSource] = useState(APP_SOURCE_ENUM.BEAM);
  const [proceedLoading, setProceedLoading] = useState(false);
  const [formData, setFormData] = useState(getInitialFormData());

  const handleActionChange = (moduleKey, action) => {
    setFormData(prev => ({
      ...prev,
      [moduleKey]: {
        ...prev[moduleKey],
        action,
        start_date: action === MODULE_ACTION_ENUM.DISABLE ? '' : prev[moduleKey].start_date,
        end_date: action === MODULE_ACTION_ENUM.DISABLE ? '' : prev[moduleKey].end_date
      }
    }));
  };

  const handleDateChange = (moduleKey, dateField, value) => {
    setFormData(prev => ({
      ...prev,
      [moduleKey]: {
        ...prev[moduleKey],
        [dateField]: value
      }
    }));
  };

  const parseApiResponse = data => {
    const newFormData = getInitialFormData();

    BEAM_MODULES.forEach(({ key }) => {
      if (data[key]) {
        newFormData[key] = {
          action: data[key].action ?? MODULE_ACTION_ENUM.DISABLE,
          start_date: data[key].start_date || '',
          end_date: data[key].end_date || ''
        };
      }
    });

    return newFormData;
  };

  const handleProceed = async () => {
    if (!email) {
      showToast('Please enter an email address', 'error');
      return;
    }

    setProceedLoading(true);

    try {
      const response = await fetchApiData({
        apiRequest: 'GET',
        endpoint: GET_ENABLE_WINDOW,
        params: { user_email: email, source }
      });

      if (response.error) {
        showToast(response.error || 'Failed to fetch module settings', 'error');
        return;
      }

      const { company_id } = response?.data || {};
      setCompanyId(company_id);
      setFormData(parseApiResponse(response?.data || {}));

      showToast('Module settings fetched successfully', 'success');
    } catch (error) {
      showToast('Failed to fetch module settings', 'error');
    } finally {
      setProceedLoading(false);
    }
  };

  const handleClear = () => {
    setEmail('');
    setCompanyId(null);
    setSource(APP_SOURCE_ENUM.BEAM);
    setFormData(getInitialFormData());
    showToast('Form cleared successfully', 'success');
  };

  const handleSave = async () => {
    if (!companyId) {
      showToast('Please fetch module settings first', 'error');
      return;
    }

    // Validate that enabled modules have dates
    for (const { key, label } of BEAM_MODULES) {
      const module = formData[key];
      if (module.action !== MODULE_ACTION_ENUM.DISABLE) {
        if (!module.start_date || !module.end_date) {
          showToast(`Please provide start and end dates for ${label}`, 'error');
          return;
        }
      }
    }

    setLoading(true);

    try {
      const payload = {
        company_id: companyId,
        source
      };

      // Build payload for each module
      BEAM_MODULES.forEach(({ key }) => {
        const module = formData[key];
        if (module.action === MODULE_ACTION_ENUM.DISABLE) {
          payload[key] = { action: module.action };
        } else {
          payload[key] = {
            action: module.action,
            start_date: module.start_date,
            end_date: module.end_date
          };
        }
      });

      const response = await fetchApiData({
        endpoint: MANAGE_ENABLE_WINDOW,
        apiRequest: 'POST',
        data: payload
      });

      if (response.error) {
        showToast(response.error || 'Failed to update module settings', 'error');
        return;
      }

      showToast('Module settings updated successfully', 'success');
    } catch (error) {
      showToast('Failed to update module settings', 'error');
    } finally {
      setLoading(false);
    }
  };

  const isEmailDisabled = !!companyId;
  const buttonText = companyId ? 'Clear' : 'Proceed';
  const handleEmailAction = companyId ? handleClear : handleProceed;

  return (
    <FormContainer>
      <div className='border border-gray-200 rounded-lg p-6 bg-white w-[420px]'>
        <AppSourceSection source={source} onSourceChange={setSource} disabled={isEmailDisabled} />

        <EmailInputSection
          email={email}
          buttonText={buttonText}
          loading={proceedLoading}
          onEmailChange={setEmail}
          onProceed={handleEmailAction}
          isEmailDisabled={isEmailDisabled}
        />

        {BEAM_MODULES.map(module => (
          <ModuleRow
            module={module}
            key={module.key}
            disabled={!companyId}
            onDateChange={handleDateChange}
            moduleData={formData[module.key]}
            onActionChange={handleActionChange}
          />
        ))}

        <div className='flex justify-center mt-4'>
          <Button color='primary' onClick={handleSave} disabled={!companyId} loading={loading} className='px-8'>
            Save
          </Button>
        </div>
      </div>
    </FormContainer>
  );
};

export default BeamModuleManagement;
